<template>
  <div
    class="my-[24px]"
  >
    <card-warehouse
      v-if="selectedWarehouse"
      no-hover
      :data="selectedWarehouse"
      :selected="false"
      :disabled="true"
    />
    <b-input-group class="group my-[16px]">
      <b-input-group-append>
        <div class="flex items-center justify-center w-[40px] border-[1px] border-[#E2E2E2] rounded-l-[8px] !border-r-0 transition-colors group-focus-within:border-[#F95031]">
          <span class="k-search-normal-1 text-[20px]" />
        </div>
      </b-input-group-append>
      <b-form-input
        v-model="searchStoreAddress"
        placeholder="Cari alamat toko"
        class="border-[1px] border-[#E2E2E2] rounded-r-[8px] !border-l-0 transition-colors group-focus-within:border-[#F95031]"
        @input="handleSearchStoreAddress"
      />
    </b-input-group>

    <div
      v-if="!isLoading && (unfilteredData.length === 0 || isError)"
      class="flex flex-col items-center justify-center gap-[8px] py-[26px] px-[8px]"
    >
      <img
        src="https://storage.googleapis.com/komerce/assets/icons/warning.svg"
        alt="warning"
        class="size-[40px] mb-[8px]"
      >
      <p
        v-if="unfilteredData.length === 0 && !isError"
        class="text-[#828282] text-[14px] mb-0 text-center"
      >
        Toko {{ shopName }} belum mempunyai alamat, mohon tambahkan alamat toko di dashboard marketplace terkait untuk melanjutkan mapping gudang
      </p>
      <p
        v-if="isError"
        class="text-[#828282] text-[14px] mb-0 text-center"
      >
        Terjadi kesalahan saat mengambil data alamat toko, silahkan refresh halaman untuk mencoba kembali
      </p>
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center gap-[8px] py-[26px] px-[8px]"
    >
      <b-spinner
        small
        variant="primary"
        label="memuat data alamat toko"
      />
      <p class="text-[#828282] text-[14px] mb-0 text-center">
        Memuat data alamat toko...
      </p>
    </div>

    <div
      v-if="cannotFindStoreAddress"
      class="flex flex-col items-center justify-center gap-[8px] py-[26px] px-[8px]"
    >
      <p class="text-[#828282] text-[14px] mb-0 text-center">
        Alamat toko <em>'{{ searchStoreAddress }}'</em> belum ditemukan. Coba gunakan kata kunci lain.
      </p>
    </div>

    <div class="max-h-[300px] overflow-y-auto pr-[4px] -mr-[4px]">
      <card-store-address
        v-for="storeAddress in data"
        :key="`${storeAddress.id}-${storeAddress.no_address}`"
        :index="storeAddress.no_address"
        :selected="checkIsSelected(storeAddress)"
        :disabled="checkIsDisabled(storeAddress)"
        :data="storeAddress"
        @click="handleCardClick(storeAddress)"
      />
    </div>
  </div>
</template>

<script>
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import CardStoreAddress from './CardStoreAddress.vue'
import CardWarehouse from './CardWarehouse.vue'

export default {
  name: 'WidgetListStoreAddress',
  components: {
    CardStoreAddress,
    CardWarehouse,
  },
  props: {
    usedWarehouses: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectedWarehouse: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data() {
    return {
      unfilteredData: [],
      filteredData: undefined,
      isLoading: false,
      isError: false,
      selectedStoreAddress: null,
      searchStoreAddress: '',
      shopId: null,
      shopName: null,
    }
  },
  computed: {
    data() {
      return this.filteredData || this.unfilteredData
    },
    cannotFindStoreAddress() {
      return this.searchStoreAddress && this.filteredData?.length === 0
    },
  },
  created() {
    this.debouncedFetchStoreAddress = _.debounce(async search => {
      await this.searchStoreAddressFn(search)
    }, 500)
  },
  mounted() {
    if (this.shopId) {
      this.getListAddress({ shop_id: this.shopId })
    }
  },
  methods: {
    /**
     * Set the shop info. This method used by the parent to set the shop info.
     * @param {String} shopId - the shop id
     * @param {String} shopName - the shop name
     */
    setShopInfo(shopId, shopName) {
      if (shopId === this.shopId) return
      this.shopId = shopId
      this.shopName = shopName
      this.getListAddress({ shop_id: shopId })
    },
    /**
     * Handle the card click event
     * @param {Object} storeAddress - the store address
     */
    handleCardClick(storeAddress) {
      this.selectedStoreAddress = storeAddress
      this.$emit('select', storeAddress)
    },
    /**
     * Check if the store address is selected
     * @param {Object} storeAddress - the store address
     * @returns {Boolean} - true if the store address is selected, false otherwise
     */
    checkIsSelected(storeAddress) {
      return this.selectedStoreAddress?.address_id === storeAddress.address_id
    },
    /**
     * Check if the store address is disabled
     * @param {Object} storeAddress - the store address
     * @returns {Boolean} - true if the store address is disabled, false otherwise
     */
    checkIsDisabled(storeAddress) {
      return this.usedWarehouses?.some(warehouse => warehouse.address_id === String(storeAddress.address_id))
    },
    /**
     * Handle the search store address input event
     * @param {Event} event - the input event
     */
    async handleSearchStoreAddress(event) {
      const search = event || ''
      if (!search) {
        this.searchStoreAddress = ''
        this.filteredData = undefined
        return
      }

      await this.debouncedFetchStoreAddress(search)
    },
    /**
     * Handle the search store address data function
     * @param {String} search - the search query
     */
    async searchStoreAddressFn(search) {
      const searchResult = this.data.filter(address => address?.address?.toLowerCase().includes(search.toLowerCase()))
      if (searchResult.length > 0) {
        this.filteredData = searchResult

        return
      }

      this.searchStoreAddress = search
      this.filteredData = []
    },
    /**
     * Get the list of store address
     * @param {String} shop_id - the shop id
     */
    async getListAddress({ shop_id }) {
      this.isLoading = true
      try {
        const response = await this.$http_new.get(`/auth/api/v1/marketplaces/shopee/shops/${shop_id}/addresses`)
        const { data } = response.data

        this.unfilteredData = data.address_list.map((address, index) => ({
          ...address,
          no_address: index + 1,
        }))
      } catch (error) {
        this.isError = true
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
